import React from 'react';
import styled from 'styled-components';
import _throttle from 'lodash/throttle';
import PremiumCTABar from './components/PremiumCTABar';
import isOneOfThemInViewport from 'Jsx/Utils/isOneOfThemInViewport';
import mixpanel from 'Jsx/Utils/mixpanelTracking';

const Fixed = styled.div<{ isVisible: boolean }>`
  bottom: 0;
  left: 0;
  right: 0;
  ${props => !props.isVisible && 'z-index: -10;'}
  ${({ theme }) => theme.breakpoints.up('sm')} {
    left: unset;
    bottom: 0;
    right: 0;
  }
`;

const ButtonWrapper = styled.div<{ isVisible: boolean }>`
  margin-top: 8px;
  transform: translateY(${props => (props.isVisible ? '0%' : '200%')});
  opacity: ${props => (props.isVisible ? '1' : '0')};
  transition: 0.3s;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-top: 24px;
  }
`;

/**
 * Conditional visibility supported if you pass a list of domIds where you don't want to
 * let this component be visible. If any of DOM Element listed at domIds is in the viewport,
 * the component will be invisible.
 * @param domIds List of DOM Elements IDs at where you don't want to show this component
 */
const FixedPremiumBar: React.FC<{
  message: string;
  buttonLabel: string;
  onClick(): void;
  domIds: string[];
}> = ({ message, buttonLabel, onClick, domIds = [] }) => {
  const defaultDOMIds = [
    'main-footer-copy-and-legals',
    'mobile-main-footer-copy-and-legals',
  ];
  const [hasRendered, setHasRendered] = React.useState(false);
  const [isVisible, setVisibility] = React.useState(domIds.length === 0);

  React.useEffect(() => {
    const DOMIds = defaultDOMIds.concat(domIds);

    if (DOMIds.length > 0) {
      // requestAnimationFrame
      const raf =
        window.requestAnimationFrame ||
        window.webkitRequestAnimationFrame ||
        window.mozRequestAnimationFrame ||
        function(callback) {
          window.setTimeout(callback, 1000 / 60);
        };
      const handler = _throttle(() => {
        raf(() => {
          if (!isOneOfThemInViewport(DOMIds)) {
            setVisibility(true);
            if (!hasRendered) {
              setHasRendered(true);
            }
          } else {
            setVisibility(false);
          }
        });
      }, 350);

      handler();
      window.addEventListener('scroll', handler);
    }
  }, []);

  React.useEffect(() => {
    if (hasRendered) {
      mixpanel.track(mixpanel.EVENTS.RENDER_COMPONENT, {
        component: 'Fixed premium bar',
      });
    }
  }, [hasRendered]);

  return (
    <Fixed
      isVisible={isVisible}
      className="fixed w-full z-dialog overflow-y-hidden"
    >
      <ButtonWrapper isVisible={isVisible}>
        <PremiumCTABar
          message={message}
          buttonLabel={buttonLabel}
          onClick={onClick}
        />
      </ButtonWrapper>
    </Fixed>
  );
};

export default FixedPremiumBar;
